import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'react-bootstrap';
import { capitalize, isEmpty, lowerFirst } from 'lodash';

import { getFullName } from 'modules/common/helpers';
import { BequestAssetCard } from 'modules/asset/components';

import { ASSET_CATEGORIES } from 'modules/estatePlan/constants';

import iconDelete from 'images/icon-delete-red.svg';
import iconMenu from 'images/icon-menu.svg';
import { ReactComponent as IconEdit } from 'images/icon-edit.svg';
import iconTick from 'images/tick.svg';

interface BequestCardProps {
  bequestData: BequestDTO;
  onDelete?: (id: string, title: string) => void;
  onEdit?: (assetItem: BequestDTO) => void;
  className?: string;
  isDropdownMenuHidden?: boolean;
  isSpousalWill?: boolean;
}

function BequestCard(props: BequestCardProps) {
  const {
    onDelete,
    onEdit,
    bequestData,
    className,
    isDropdownMenuHidden,
    isSpousalWill,
  } = props;
  const { t } = useTranslation(['common', 'will', 'dashboard']);

  const heirs: ContactDTO[] =
    useSelector((state: any) => state.heirs.list) || [];
  const heirTo = (heirs.find((item) => item.id === bequestData.to) ||
    {}) as ContactDTO;
  const isIncluded = bequestData.include === 'true';

  const bequestedByOptions = [
    { value: 'partner', label: t('common:partner') },
    { value: 'myself', label: t('common:myself') },
  ];
  const bequestedBy = bequestedByOptions.find(
    (item) => item.value === bequestData.from
  );

  const categoryOptions = Object.values(ASSET_CATEGORIES).map((item) => ({
    value: item,
    label: capitalize(t(`dashboard:assets_${item}_tab`)),
  }));
  const categoryOption = categoryOptions.find(
    (item) => item.value === lowerFirst(bequestData.assetCategory)
  );
  const categoryLabel = categoryOption ? categoryOption.label : '';

  return (
    <>
      <div className={className || 'card t-p-6'}>
        <div className="Character Character--human">
          <div className="Character-menu">
            <Dropdown className="dropdown--settings">
              {!isDropdownMenuHidden && (
                <Dropdown.Toggle variant="success" id="dropdown-settings">
                  <img src={iconMenu} alt={t('common:alt_dropdown_menu')} />
                </Dropdown.Toggle>
              )}

              <Dropdown.Menu className="t-m-0">
                {onEdit && (
                  <Dropdown.Item onClick={() => onEdit(bequestData)}>
                    <IconEdit className="dropdown-icon t-text-beta-200" />
                    {t('common:edit_label')}
                  </Dropdown.Item>
                )}

                {onDelete && (
                  <Dropdown.Item
                    onClick={() =>
                      onDelete(bequestData.id || '', bequestData.bequest)
                    }
                  >
                    <img
                      src={iconDelete}
                      alt="delete"
                      role="presentation"
                      className="dropdown-icon dropdown-icon--delete"
                    />
                    {t('common:delete_label')}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          <div className="Character-footer d-flex flex-column">
            {bequestData.assetCategory ? (
              <div style={{ width: '95%' }}>
                <BequestAssetCard
                  assetCategory={categoryLabel}
                  assetTitle={bequestData.bequest}
                />
              </div>
            ) : (
              <div>
                <p className="typo-zeta">{t('will:legacy')}</p>
                <p className="typo-beta">{bequestData.bequest}</p>
              </div>
            )}
            {isSpousalWill && bequestedBy ? (
              <div className="t-pt-2.5 t-pb-3">
                <>
                  <p className="typo-zeta">{t('will:bequested_by')}</p>
                  <p className="typo-beta">{bequestedBy.label}</p>
                </>
              </div>
            ) : (
              <div className="u-pb-10" />
            )}
            {!isEmpty(heirTo) && (
              <div>
                <p className="typo-zeta">{t('will:bequest_go_to')}</p>
                <p className="typo-beta">{getFullName(heirTo, false)}</p>
              </div>
            )}
            <div className="d-flex" style={{ alignItems: 'baseline' }}>
              {isIncluded && (
                <img
                  src={iconTick}
                  alt=""
                  role="presentation"
                  className="u-pr-10"
                />
              )}
              <p className="typo-zeta t-pt-2.5 ">
                {isIncluded
                  ? t('will:included_into_bequest')
                  : t('will:not_included_into_bequest')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="u-pb-10" />
    </>
  );
}

export default BequestCard;
